import { useState, useEffect, useCallback } from "react";
import { Sparkles, ArrowRight, Mail, Lock, ChevronDown, AlertCircle, RefreshCw } from "lucide-react";
import { useToolAccess } from "@/hooks/useToolAccess";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { analytics } from "@/hooks/useAnalytics";

const COUNTRIES = [
  "United States", "United Kingdom", "Canada", "Australia", "Germany", "France", 
  "India", "Brazil", "Mexico", "Spain", "Italy", "Netherlands", "Sweden", 
  "Switzerland", "Japan", "Singapore", "UAE", "South Africa", "Other"
];

const formSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required").max(100, "Full name too long"),
  email: z.string().trim().email("Please enter a valid email").max(255, "Email too long"),
  country: z.string().min(1, "Please select your country"),
});

interface ToolAccessGateProps {
  toolId: string;
  toolName: string;
  children: React.ReactNode | ((props: { userEmail: string | null; userName: string | null; sessionToken: string | null }) => React.ReactNode);
}

const SESSION_KEY = "itdukes_tool_session";
const RESEND_COOLDOWN_SECONDS = 60;

export const ToolAccessGate = ({ toolId, toolName, children }: ToolAccessGateProps) => {
  const { isVerified, isLoading, requestOTP, verifyOTP, logToolUsage, email: userEmail, fullName: userName } = useToolAccess();
  const { toast } = useToast();
  
  const [step, setStep] = useState<"form" | "otp">("form");
  const [formFullName, setFormFullName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [country, setCountry] = useState("");
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState<{ fullName?: string; email?: string; country?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasLoggedUsage, setHasLoggedUsage] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown <= 0) return;
    
    const timer = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCountdown]);

  // Log tool usage when verified
  useEffect(() => {
    if (isVerified && !hasLoggedUsage) {
      logToolUsage(toolId, toolName);
      setHasLoggedUsage(true);
    }
  }, [isVerified, hasLoggedUsage, logToolUsage, toolId, toolName]);

  const handleResendOTP = useCallback(async () => {
    if (resendCountdown > 0 || isResending) return;
    
    setIsResending(true);
    try {
      await requestOTP(formEmail, formFullName, country);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      toast({
        title: "Code resent!",
        description: "Check your email for the new 6-digit code.",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to resend code";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  }, [formEmail, formFullName, country, resendCountdown, isResending, requestOTP, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isVerified) {
    const sessionToken = sessionStorage.getItem(SESSION_KEY);
    if (typeof children === "function") {
      return <>{children({ userEmail, userName, sessionToken })}</>;
    }
    return <>{children}</>;
  }

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = formSchema.safeParse({ fullName: formFullName, email: formEmail, country });
    if (!result.success) {
      const fieldErrors: typeof errors = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === "fullName") fieldErrors.fullName = err.message;
        if (err.path[0] === "email") fieldErrors.email = err.message;
        if (err.path[0] === "country") fieldErrors.country = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await requestOTP(formEmail, formFullName, country);
      setStep("otp");
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
      toast({
        title: "Verification code sent!",
        description: "Check your email for the 6-digit code.",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send verification code";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter the 6-digit code from your email",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await verifyOTP(formEmail, otp);
      analytics.leadFormSubmitted(`tool_access_${toolName}`);
      toast({
        title: "Verified!",
        description: "You now have access to all tools.",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Invalid or expired code";
      toast({
        title: "Verification failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-8 px-4">
      <div className="bg-card rounded-2xl border border-border shadow-xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary/10 via-purple-500/10 to-cyan-500/10 p-6 border-b border-border">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            {step === "form" ? (
              <Lock className="w-7 h-7 text-primary" />
            ) : (
              <Mail className="w-7 h-7 text-primary" />
            )}
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground mb-2">
            {step === "form" ? "Unlock Free Tools" : "Enter Verification Code"}
          </h2>
          <p className="text-muted-foreground text-sm">
            {step === "form" 
              ? "Quick verification to access all email deliverability tools - no payment required."
              : `We sent a 6-digit code to ${formEmail}`
            }
          </p>
        </div>

        {/* Form */}
        <div className="p-6">
          {step === "form" ? (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-1.5">
                  Full Name
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={formFullName}
                  onChange={(e) => setFormFullName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
                {errors.fullName && (
                  <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.fullName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                />
                {errors.email && (
                  <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-foreground mb-1.5">
                  Country
                </label>
                <div className="relative">
                  <select
                    id="country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground appearance-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  >
                    <option value="">Select your country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
                {errors.country && (
                  <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.country}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                    Sending Code...
                  </>
                ) : (
                  <>
                    Get Verification Code
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-foreground mb-1.5">
                  Verification Code
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-full px-4 py-4 rounded-xl border border-border bg-background text-foreground text-center text-2xl font-mono tracking-[0.5em] placeholder:text-muted-foreground placeholder:tracking-normal placeholder:text-base focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  maxLength={6}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting || otp.length !== 6}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Access Tools
                    <Sparkles className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep("form")}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Use a different email
              </button>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendCountdown > 0 || isResending}
                className="w-full flex items-center justify-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors disabled:text-muted-foreground disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Sending...
                  </>
                ) : resendCountdown > 0 ? (
                  <>
                    <RefreshCw className="w-3 h-3" />
                    Resend code in {resendCountdown}s
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3 h-3" />
                    Resend verification code
                  </>
                )}
              </button>
            </form>
          )}

          <p className="text-xs text-muted-foreground text-center mt-4">
            By continuing, you agree to receive email updates from IT Dukes.
          </p>
        </div>
      </div>
    </div>
  );
};